import { API_URL } from '@/lib/api';

/**
 * The suite's office tools — yaz (documents), diz (spreadsheets), sun (presentations),
 * baz (databases/notes) — addressed as deep links. The bridge inside each app reads
 * the params, pulls the bytes from the pooled vault with the shared session cookie,
 * and offers a "Save to Sak" door for the current document.
 */

export type OfficeAppId = 'yaz' | 'diz' | 'sun' | 'baz';

export interface OfficeApp {
  id: OfficeAppId;
  label: string;
  kind: 'document' | 'spreadsheet' | 'presentation' | 'database';
  /** Extensions this app edits (without the dot). */
  extensions: string[];
  /** Seed name the drive creates for "New" — the app binds its identity to it. */
  newExtension: string;
  portEnv?: string;
  defaultPort: number;
}

export const OFFICE_APPS: OfficeApp[] = [
  { id: 'yaz', label: 'Yaz', kind: 'document', extensions: ['md', 'markdown', 'txt', 'fountain'], newExtension: 'md', defaultPort: 6119, portEnv: 'SAK_OFFICE_YAZ_URL' },
  { id: 'diz', label: 'Diz', kind: 'spreadsheet', extensions: ['xlsx', 'xls', 'csv', 'tsv'], newExtension: 'xlsx', defaultPort: 6107, portEnv: 'SAK_OFFICE_DIZ_URL' },
  { id: 'sun', label: 'Sun', kind: 'presentation', extensions: ['sun', 'sunp', 'pptx'], newExtension: 'sun', defaultPort: 6111, portEnv: 'SAK_OFFICE_SUN_URL' },
  { id: 'baz', label: 'Baz', kind: 'database', extensions: ['md', 'markdown', 'dbaz'], newExtension: 'md', defaultPort: 6121, portEnv: 'SAK_OFFICE_BAZ_URL' },
];

function originOf(app: OfficeApp): string | null {
  // In the suite the office tools live on their own localhost ports; the env override
  // covers installs that moved them (reverse proxy, remote host). On a deployed
  // public origin a loopback deep link would point the VISITOR's browser at their
  // own machine, so an un-injected loopback default resolves to null there and
  // the callers no-op instead of opening a dead tab.
  const injected = (process.env as Record<string, string | undefined>)[app.portEnv ?? ''];
  if (injected) return injected.replace(/\/$/, '');
  if (
    typeof window !== 'undefined' &&
    !/^(localhost|127\.0\.0\.1|\[::1\]|::1|tauri\.localhost)$/i.test(window.location.hostname)
  ) {
    return null;
  }
  return `http://127.0.0.1:${app.defaultPort}`;
}

/** The apps office tools can open, filtered by the file's extension. */
export function appsForFile(name: string): OfficeApp[] {
  const extension = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  return OFFICE_APPS.filter((app) => app.extensions.includes(extension));
}

/** Deep link that makes the app pull this vault file and bind it for saving back. Null when the office app is unreachable from this page. */
export function openInOfficeUrl(app: OfficeApp, fileId: string, name: string): string | null {
  const origin = originOf(app);
  return origin
    ? `${origin}/?vaultFileId=${encodeURIComponent(fileId)}&vaultName=${encodeURIComponent(name)}`
    : null;
}

/** Deep link for a brand-new document: the app starts blank and saves back as a new file. Null when the office app is unreachable from this page. */
export function newInOfficeUrl(app: OfficeApp, name: string): string | null {
  const origin = originOf(app);
  return origin ? `${origin}/?vaultNew=${encodeURIComponent(name)}` : null;
}

/** The vault URL each app's bridge downloads from — exported so tests stay honest. */
export function vaultDownloadUrl(fileId: string): string {
  return `${API_URL.replace(/\/$/, '')}/api/files/${fileId}/download`;
}

/** The upload door a bridge uses when saving a brand-new document back to the drive. */
export function vaultUploadUrl(): string {
  return `${API_URL.replace(/\/$/, '')}/api/files/upload`;
}

/** The versioned content door for saving over an existing file. */
export function vaultContentUrl(fileId: string): string {
  return `${API_URL.replace(/\/$/, '')}/api/files/${fileId}/content`;
}

/** Public request link shown to receivers (they need no account). */
export function fileRequestUrl(shortId: string): string {
  return `${window.location.origin}/request/${encodeURIComponent(shortId)}`;
}
